import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormArray,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Id } from '@core/http/crud-model';
import { FilterParams, PaginationParams, SortingParams, TabParams } from '@data/filtering/filtering.interface';
import { FleetResource } from '@data/fleets/fleets.model';
import { NoteResource } from '@data/note/note.model';
import { SuborderItemResource } from '@data/orders-with-reservation/suborders/orders-with-reservation-suborders.model';
import { ProductTyreResource } from '@data/product-tyres/product-tyres.model';
import { RecipientContactPersonResource, RecipientResource } from '@data/recipients/recipients.model';
import { RelocationResource } from '@data/relocations/relocations.model';
import { Suborder, suborderFactory } from '@data/suborder/suborder.model';
import { UserResource } from '@data/users/users.model';
import { VehicleResource } from '@data/vehicles/vehicles.model';
import { WorkshopResource } from '@data/workshop/workshop.model';
import { DateTimeString } from '@shared/interfaces/date-string';
import { WholesalerResource } from '@data/wholesalers/wholesalers.model';

export type OrderStatusValue =
  | 'new'
  | 'confirmed'
  | 'delivered'
  | 'ready_for_pickup'
  | 'pickup_ordered'
  | 'received'
  | 'finished'
  | 'cancelled'
  | 'rejected'
  | 'substitutes'
  | 'expired';

export class OrderStatus {
  static NEW: OrderStatusValue = 'new'; // Nowe
  static CONFIRMED: OrderStatusValue = 'confirmed'; // Potwierdzone
  static DELIVERED: OrderStatusValue = 'delivered'; // Dostarczone
  static READY_FOR_PICKUP: OrderStatusValue = 'ready_for_pickup'; // Gotowe do odbioru
  static PICKUP_ORDERED: OrderStatusValue = 'pickup_ordered'; // Zlecony odbiór
  static RECEIVED: OrderStatusValue = 'received'; // Opony odebrane
  static FINISHED: OrderStatusValue = 'finished'; // Zakończone
  static CANCELLED: OrderStatusValue = 'cancelled'; // Anulowane
  static REJECTED: OrderStatusValue = 'rejected'; // Odrzucone
  static SUBSTITUTES: OrderStatusValue = 'substitutes'; // Zamienniki
  static EXPIRED: OrderStatusValue = 'expired'; // Przeterminowane

  static color(status: OrderStatusValue): string {
    switch (status) {
      case OrderStatus.NEW:
        return '#04CFEB';
      case OrderStatus.CONFIRMED:
        return '#0055FF';
      case OrderStatus.DELIVERED:
        return 'var(--signal-success-green)';
      case OrderStatus.READY_FOR_PICKUP:
        return 'var(--supporting-4-purple)';
      case OrderStatus.PICKUP_ORDERED:
        return 'var(--supporting-8-olive)';
      case OrderStatus.RECEIVED:
        return 'var(--primary-b-blue)';
      case OrderStatus.FINISHED:
        return 'var(--supporting-7-navy)';
      case OrderStatus.CANCELLED:
        return 'var(--ui-g4-gray4)';
      case OrderStatus.REJECTED:
        return 'var(--supporting-1-orange)';
      case OrderStatus.EXPIRED:
        return '#f5812e';
      case OrderStatus.SUBSTITUTES:
        return 'var(--supporting-7-lighten20)';
      default:
        return 'var(--ui-g1-gray1)';
    }
  }
}

export type SuborderTypeSlug = 'with-reservation' | 'disposition-sale' | 'zam-sale';

export class SuborderType {
  public static WITH_RESERVATION: SuborderTypeSlug = 'with-reservation';
  public static DISPOSITION_SALE: SuborderTypeSlug = 'disposition-sale';
  public static ZAM_SALE: SuborderTypeSlug = 'zam-sale';
}

export interface SupplierCreationStatus {
  value: string;
  description: string;
}

export interface SuborderResource {
  id: number;
  number: string;
  supplier_number: string;
  user: UserResource;
  workshop: WorkshopResource;
  ordered_item: SuborderItemResource;
  substitutes: SuborderItemResource[];
  items: SuborderItemResource[];
  is_with_reservation: boolean;
  vehicle: VehicleResource;
  vehicle_registration_number: string;
  vehicle_vin: string;
  fleet: FleetResource;
  fleet_name: string;
  notes: string;
  status: OrderStatusValue;
  supplier_creation_status?: SupplierCreationStatus;
  status_changed_at: string;
  response_time_until: string;
  type: SuborderTypeSlug;
  created_at: string;
  updated_at: string;
  confirmed: SuborderItemResource;
  confirmed_at: DateTimeString;
  pickup_status: string;
  with_pickup: boolean;
  realization_time: string;
  recipient: RecipientResource;
  order_number: string;
  order: OrderResource;
  total_wholesaler_price: string;
  import_data: ImportData;
  import_data_wholesaler: WholesalerResource;
  stock_id: number;
  quantity: number;
  waybills?: SuborderWaybillResource[] | null;
}

export interface SuborderWaybillResource {
  id: number;
  shipment_number: string;
  courier: string;
  tracking_url: string | null;
  shipped_at?: DateTimeString;
}

export interface ImportData {
  auth_code: string;
  comment: string;
  contact_person: string;
  ean: string;
  external_order_number: string;
  order_with_reservation: string;
  quantity: number;
  recipient_code: string;
  registration_number: string;
  sap_code: string;
  vin: string;
  wholesaler_code: string;
  with_pickup: string;
}

export type OrderProductTyreResource = ProductTyreResource;
export type OrderProductTyreResourceFilterParams = FilterParams;

export interface OrderResource {
  id: Id;
  user: UserResource;
  confirmed: SuborderItemResource;
  number: string;
  recipient: RecipientResource;
  recipient_contact_person: RecipientContactPersonResource;
  workshop: WorkshopResource;
  suborders: SuborderResource[];
  status: OrderZamStatusResource;
  relocation: RelocationResource;
  status_changed_at: string;
  created_at: string;
  updated_at: string;
  register_fzo: boolean;
  auth_code: string;
  vehicle: VehicleResource;
  vehicle_registration_number: string;
  vehicle_vin: string;
  order_number: string;
  fleet: FleetResource;
  fleet_name: string;
  notes: NoteResource[];
  suborders_count: number;
  suborders_status_count: number;
  import_id: number;
  import_data: ImportData;
  import_data_wholesaler: WholesalerResource;
}

export interface OrdersMakingFilterParams extends PaginationParams, SortingParams, TabParams {
  keyword: string;
  ean_sap_code: string;
  strict_ean_sap_code: string;
  code: string;
  fleet_id: string;
  wholesalers: string;
  producers: string;
  rolling_resistances: string;
  tyre_params: string;
  supplier_code: string;
  workshop_id: string;
  tyre_size: string;
  tyre_season: string;
  run_flat: boolean;
  adhesion: string;
  noise_level: string;
  load_index: string;
  speed_index: string;
  price_from: string;
  price_to: string;
  quantity_from: string;
  quantity_to: string;
  tread_model: string;
  year: string;
  has_reinforcement: boolean;
  has_protector: boolean;
  tyre_type: string;
  realization_time_max: string;
}

export type OrdersFilterParams = FilterParams;
export type SubordersFilterParams = FilterParams;

export interface OrderPostRequest {
  workshop_code: string;
  recipient_code: string;
  recipient_contact_person_id: number;
  vehicle_id: number;
  fleet_id: number;
  external_order_number: string;
  with_pickup: boolean;
  register_fzo: boolean;
  auth_code: string;
  items: OrderItemRequest[];
  for_import?: number;
}

export interface OrderItemRequest {
  stock_id: number;
  quantity?: number;
  suborder_id?: number;
}

export type OrderPatchRequest = Partial<OrderPostRequest>;

export interface OrderZAMSummaryResource {
  orders_count: number;
  ordered_quantity: number;
  fleet_price_sum: string;
  catalog_price_sum: string;
}

export type Orderable = WorkshopResource | RecipientResource;

export enum OrderableType {
  WORKSHOP = 'workshop',
  RECIPIENT = 'recipient',
}

export type OrderFormGroup = FormGroup<{
  recipient: FormGroup<{
    code: FormControl<string>;
    contact_person_id: FormControl<string>;
  }>;
  fleet_id: FormControl<string>;
  with_pickup: FormControl<boolean>;
  register_fzo: FormControl<boolean>;
  auth_code: FormControl<string>;
  vehicle_data: FormGroup<{
    registration_number: FormControl<string>;
    vin_number: FormControl<string>;
    external_order_number: FormControl<string>;
  }>;
  suborders: FormArray<SuborderFormGroup>;
}>;

export type SuborderFormGroup = FormGroup<{
  stock_id: FormControl<number>;
  quantity: FormControl<number>;
}>;

export class OrderWithForm {
  private readonly form: OrderFormGroup;
  private firstSuborder: Suborder;

  constructor(private order: OrderResource) {
    this.firstSuborder = order.suborders.map(suborderFactory)[0] || null;
    this.form = new FormGroup({
      recipient: new FormGroup(
        {
          code: new FormControl(this.order.recipient?.code?.toString() || this.order.workshop?.code?.toString(), {
            updateOn: 'change',
            validators: [Validators.required],
          }),
          contact_person_id: new FormControl<string>(this.order?.recipient_contact_person?.id?.toString(), {
            updateOn: 'change',
          }),
        },
        { validators: [contactPersonRequiredUnlessCodeContainsPS()] },
      ),
      fleet_id: new FormControl<string>(this.order?.fleet?.id?.toString(), {
        validators: [Validators.required],
      }),
      register_fzo: new FormControl<boolean>(this.order?.register_fzo),
      auth_code: new FormControl<string>(this.order?.auth_code, { updateOn: 'blur' }),
      with_pickup: new FormControl<boolean>(this.firstSuborder?.isWithPickup()),
      vehicle_data: new FormGroup(
        {
          vin_number: new FormControl<string>(this.order?.vehicle_vin),
          external_order_number: new FormControl<string>(this.order?.order_number),
          registration_number: new FormControl<string>(this.order?.vehicle_registration_number),
        },
        { validators: [atLeastOneVehicleFieldRequired()] },
      ),

      suborders: new FormArray(
        this.order.suborders.map(
          (s: SuborderResource) =>
            new FormGroup({
              stock_id: new FormControl(s.stock_id),
              quantity: new FormControl(s.quantity, { updateOn: 'blur' }),
            }),
        ),
      ),
    });
  }

  getId(): Id {
    return this.order?.id;
  }

  getResource(): OrderResource {
    return this.order;
  }

  getForm(): OrderFormGroup {
    return this.form;
  }
}

export const orderWithFormFactory = (order: OrderResource): OrderWithForm => new OrderWithForm(order);

export const contactPersonRequiredUnlessCodeContainsPS = (): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const code = group.get('code')?.value;
    const contactPerson = group.get('contact_person_id')?.value;

    if (code?.includes('PS')) {
      return null; // OK
    }

    return contactPerson ? null : { contactPersonRequired: true };
  };
};

export const atLeastOneVehicleFieldRequired = (): ValidatorFn => {
  return (group: AbstractControl): ValidationErrors | null => {
    const registrationNumber = group.get('registration_number')?.value;
    const vinNumber = group.get('vin_number')?.value;
    const externalOrderNumber = group.get('external_order_number')?.value;

    if (registrationNumber || vinNumber || externalOrderNumber) {
      return null;
    }

    return { atLeastOneVehicleFieldRequired: true };
  };
};

export interface OrderZamStatusResource {
  hex: string;
  value: string;
  label: string;
}

export const orderFactory = (resource: OrderResource): Order => {
  return new Order(resource);
};

export class Order {
  constructor(private resource: OrderResource) {}

  getId(): Id {
    return this.resource?.id;
  }

  getResource(): OrderResource {
    return this.resource;
  }

  getContactPersonName(): string {
    return this.resource?.recipient_contact_person?.name;
  }

  getContactPersonPhoneNumber(): string {
    return this.resource?.recipient_contact_person?.phone_number;
  }

  getContactPersonEmail(): string {
    return this.resource?.recipient_contact_person?.email;
  }

  getSumQuantity(): number {
    return this.resource?.suborders?.reduce((accumulator, suborder) => +accumulator + suborder?.confirmed?.quantity, 0);
  }

  getStatus(): OrderZamStatusResource {
    return this.resource?.status;
  }

  getLeadTime(): string {
    return this.resource?.suborders[0]?.realization_time;
  }

  getDeliveryDate(): string {
    return this.resource?.created_at; // TODO
  }

  getPickupPointName(): string {
    return this.resource?.recipient?.name;
  }

  getPickupPointId(): string {
    return this.resource?.recipient?.id.toString();
  }

  getPickupPointType(): string {
    return 'Dealer'; // TODO
  }

  getPickupAddressPart1(): string | null {
    const zip = this.resource?.recipient?.address?.zip;
    const city = this.resource?.recipient?.address?.city;

    if (zip && city) {
      return `${zip} ${city}`;
    }

    return null;
  }

  getPickupAddressPart2(): string | null {
    const street = this.resource?.recipient?.address?.street;

    if (street) {
      return `ul. ${street}`;
    }

    return null;
  }

  getType(): string {
    return 'order';
  }

  getVehicle(): VehicleResource {
    return this.resource?.vehicle;
  }

  getOrderNumber(): string {
    return this.resource?.order_number;
  }
}
